import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import SiteDetailHero from "./SiteDetailHero";
import SiteDetailContent from "./SiteDetailContent";
import { useSiteUtils } from "./useSiteUtils";
import { useSiteDescriptions } from "./useSiteDescriptions";
import { useSiteData } from "./useSiteData";
import { addUploadedImageToSite } from "@/services/sitePhotosApi";
import { OpeningHoursSchedule } from "@/services/types";

interface ImageData {
  url: string;
  alt: string;
}

interface SiteDetailPageProps {
  title?: string;
  description?: string;
  heroImageUrl?: string;
  secondaryImageUrl?: string;
  images?: ImageData[];
  proximityInfo?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  phone?: string;
  email?: string;
  website?: string;
  openingHours?: OpeningHoursSchedule;
  services?: string[];
  coordinates?: { lat: number; lng: number };
  isMiniSite?: boolean;
}

const SiteDetailPage: React.FC<SiteDetailPageProps> = ({ 
  title, 
  description, 
  heroImageUrl,
  secondaryImageUrl,
  images,
  proximityInfo,
  address,
  postalCode,
  city,
  phone,
  email,
  website,
  openingHours,
  services,
  coordinates,
  isMiniSite = false
}) => {
  // Extraire siteId depuis les paramètres d'URL
  const { siteId } = useParams<{ siteId: string }>();
  
  const { loading, site: dbSite } = useSiteData(siteId || "");
  const { formatOpeningHours, getDirectionsUrl } = useSiteUtils();
  const { getSiteDescription } = useSiteDescriptions();

  // Créer un objet site à partir des props si toutes les données nécessaires sont fournies
  const localSite = title ? {
    id: siteId || "",
    name: title,
    description: description || "",
    address: address || "",
    postalCode: postalCode || "",
    city: city || "",
    phone: phone || "",
    email: email,
    websiteUrl: website,
    opening_hours: openingHours || {
      lundi: { matin: "", apres_midi: "" },
      mardi: { matin: "", apres_midi: "" },
      mercredi: { matin: "", apres_midi: "" },
      jeudi: { matin: "", apres_midi: "" },
      vendredi: { matin: "", apres_midi: "" },
      samedi: { matin: "", apres_midi: "" },
      dimanche: { matin: "", apres_midi: "" }
    },
    services: services || [],
    coordinates: coordinates || { lat: 0, lng: 0 }
  } : null;

  // Utiliser les données locales si elles sont disponibles, sinon utiliser les données de la BDD
  // Si le site existe dans la BDD, les données locales ont priorité sur les champs fournis
  const site = dbSite ? {
    ...dbSite,
    name: title || dbSite.name,
    description: description || dbSite.description,
    address: address || dbSite.address,
    postalCode: postalCode || dbSite.postalCode,
    city: city || dbSite.city,
    phone: phone || dbSite.phone,
    email: email || dbSite.email,
    websiteUrl: website || dbSite.websiteUrl,
    opening_hours: openingHours || dbSite.opening_hours,
    services: services || dbSite.services,
    coordinates: coordinates || dbSite.coordinates
  } : localSite;

  // Effect to handle images - now with better error handling
  useEffect(() => {
    // Only try to add photos if we actually have a valid site ID and it's not a mini-site
    if (siteId && !isMiniSite) {
      // If we have a custom images array, use it
      const imagesData = images || [];
      
      // Add hero and secondary images if they're not already in the images array
      if (heroImageUrl && !imagesData.some(img => img.url === heroImageUrl)) {
        imagesData.unshift({ url: heroImageUrl, alt: title || "Image principale du site" });
      }
      
      if (secondaryImageUrl && !imagesData.some(img => img.url === secondaryImageUrl)) {
        if (imagesData.length === 0 || (imagesData[0].url !== secondaryImageUrl)) {
          imagesData.push({ url: secondaryImageUrl, alt: "Image du site" });
        }
      }
      
      if (imagesData.length > 0) {
        addUploadedImageToSite(siteId, imagesData).catch(err => {
          console.error("Error adding uploaded images to site:", err);
          // We'll continue showing the site with the provided images even if database update fails
        });
      }
    }
  }, [siteId, heroImageUrl, secondaryImageUrl, images, title, isMiniSite]);

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title={`${title || (dbSite?.name || "Site")} - Groupe Fabre Environnement`}
        description={description || `Découvrez les services et informations de ${dbSite?.name || "notre site"} du Groupe Fabre Environnement.`}
        canonical={`/${siteId}`}
      />
      <Navbar />
      
      {/* Hero Section avec structure H1 */}
      <SiteDetailHero 
        siteName={site?.name || title || ""} 
        city={site?.city}
        services={site?.services} 
        websiteUrl={site?.websiteUrl}
        proximityInfo={proximityInfo}
        phone={site?.phone}
        email={site?.email}
        isMiniSite={isMiniSite}
      />

      {/* Main Content avec structure H2/H3 */}
      <SiteDetailContent 
        loading={loading && !site}
        site={site}
        formatOpeningHours={formatOpeningHours}
        getDirectionsUrl={getDirectionsUrl}
        getSiteDescription={getSiteDescription}
        isMiniSite={isMiniSite}
      />
      
      <Footer />
    </div>
  );
};

export default SiteDetailPage;
